import { create } from "zustand";
import type React from "react";

type HeaderData = {
	title: string;
	subtitle?: string;
	actions: React.ReactNode;
	breadcrumbs: Array<{ label: string; href?: string }>;
};

type HeaderUpdater = (previous: HeaderData) => Partial<HeaderData>;

type SetHeaderOptions = {
	preservePrevious?: boolean;
};

const initialHeaderState: HeaderData = {
	title: "",
	subtitle: undefined,
	actions: null,
	breadcrumbs: [],
};

export interface HeaderState {
	title: string;
	subtitle?: string;
	actions: React.ReactNode;
	breadcrumbs: Array<{ label: string; href?: string }>;
	setHeader: (config: Partial<HeaderData> | HeaderUpdater, options?: SetHeaderOptions) => void;
	reset: () => void;
}

/**
 * Zustand store for global dashboard header state.
 */
export const useHeaderStore = create<HeaderState>((set) => ({
	...initialHeaderState,
	setHeader: (config, options) =>
		set((state) => {
			const previous: HeaderData = {
				title: state.title,
				subtitle: state.subtitle,
				actions: state.actions,
				breadcrumbs: state.breadcrumbs,
			};

			const nextConfig = typeof config === "function" ? config(previous) : config;
			const baseState = options?.preservePrevious ? previous : initialHeaderState;

			return {
				...state,
				...baseState,
				...nextConfig,
			};
		}),
	reset: () => set(initialHeaderState),
}));
